import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  FormControl,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { paymentSettingsService } from 'components/api/paymentSettings';
import { commonGridStyles } from 'styles/gridStyles';
import styles from './Payments.module.scss';

interface WithdrawWalletProps {
  siteId: string | number;
}

const WithdrawWallet: React.FC<WithdrawWalletProps> = ({ siteId }) => {
  const [settings, setSettings] = useState<any[]>([]);
  const [selectedSettingId, setSelectedSettingId] = useState<string>('');
  const [balances, setBalances] = useState<any[]>([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoadingSettings(true);
      try {
        const response = await paymentSettingsService.getSettings(siteId);
        const payload = response.data;
        let parsedSettings = [];
        if (Array.isArray(payload)) {
          parsedSettings = payload;
        } else if (payload?.data?.settings) {
          parsedSettings = payload.data.settings;
        } else if (payload?.settings) {
          parsedSettings = payload.settings;
        }

        const withdrawSettings = parsedSettings.filter((s: any) => s.type === 'withdraw');
        setSettings(withdrawSettings);
        if (withdrawSettings.length > 0) {
          setSelectedSettingId(withdrawSettings[0].id.toString());
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setIsLoadingSettings(false);
      }
    };
    fetchSettings();
  }, [siteId]);

  useEffect(() => {
    if (!selectedSettingId) return;

    const fetchBalances = async () => {
      setIsLoadingBalances(true);
      try {
        const response = await paymentSettingsService.getWalletBalance(Number(selectedSettingId));
        const payload = response.data;
        let wallets = [];
        if (Array.isArray(payload?.wallets)) {
          wallets = payload.wallets;
        } else if (Array.isArray(payload?.data?.wallets)) {
          wallets = payload.data.wallets;
        }

        const mappedBalances = wallets.flatMap((w: any) =>
          (w.balances || []).map((b: any) => ({
            id: `${w.walletId}-${b.currencyId}`,
            currencyId: b.currencyId,
            coin: b.currencyName,
            amount: b.balance,
            paymentName: w.walletName,
          })),
        );

        setBalances(mappedBalances);
      } catch (err) {
        console.error('Failed to fetch wallet balances:', err);
        setBalances([]);
      } finally {
        setIsLoadingBalances(false);
      }
    };
    fetchBalances();
  }, [selectedSettingId]);

  const handleSettingChange = (event: SelectChangeEvent) => {
    setSelectedSettingId(event.target.value);
  };

  const columns: GridColDef[] = [
    { field: 'currencyId', headerName: 'Currency Id', width: 150 },
    { field: 'coin', headerName: 'Coin', width: 150 },
    { field: 'amount', headerName: 'Amount', width: 200 },
    { field: 'paymentName', headerName: 'Payment Name', width: 200 },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {isLoadingSettings ? (
        <CircularProgress size={24} />
      ) : settings.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl size="small" className={styles.selectContainer} sx={{ maxWidth: 200 }}>
            <Select value={selectedSettingId} onChange={handleSettingChange}>
              {settings.map((s) => (
                <MenuItem key={s.id} value={s.id.toString()}>
                  {s.paymentName} (ID: {s.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={balances}
              columns={columns}
              loading={isLoadingBalances}
              sx={commonGridStyles}
              disableRowSelectionOnClick
              hideFooter
            />
          </Box>
        </Box>
      ) : (
        <Typography color="text.secondary">No withdraw settings found.</Typography>
      )}
    </Box>
  );
};

export default WithdrawWallet;
